// AI Import — lado cliente (escreve no Firestore + resolve coords no Google).
//
// As funções PURAS (parseAiResponse, validateAiPayload, convertToInternal)
// vivem em `aiImport.js` — sem dependências de browser, importáveis no Node
// (specs E2E) e no runtime do Cloudflare (geração de viagem com IA no backend).
//
// Este módulo é o complemento que SÓ roda no browser: pega o payload já
// convertido (`internal`) e o grava de fato — cria a trip, salva os blobs,
// e corrige coordenadas via Places API. Por isso ele importa Firebase e o
// SDK do Google Maps, e NÃO pode ser importado fora do browser.

import { cloudSave } from '../../core/cloudSync.js';
import { createChecklistItem } from '../documents/checklistApi.js';
import { saveTripSettings } from '../../core/tripSettings.js';

// ====================================================================
//  IMPORT — chama tripsApi.create + cloudSave em sequência
// ====================================================================

/**
 * Cria a trip e popula todos os blobs.
 * Idempotência parcial: se `tripsApi.create` falhar, nada é gravado.
 * Se um cloudSave falhar no meio, a trip já existe e os blobs anteriores
 * foram salvos — o user pode retentar via UI futura (não escopo aqui).
 */
export async function importAiPayload(internal) {
  const tripsApi = window.tripsApi;
  if (!tripsApi?.create) throw new Error('Sistema de viagens indisponível. Recarregue a página.');

  // Pass de correção de coords via Places API — a IA chuta lat/lng e às vezes
  // erra (ex: hotel marcado no mar). Quando há `title` + lat/lng mas SEM
  // placeId, resolvemos o lugar real via `Place.searchByText({locationBias})`
  // (mesmo padrão do `resolveItineraryPlace` no mapCanvas) e sobrescrevemos.
  // Best-effort: falha em uma resolução não aborta o import.
  try {
    await resolveCoordsViaPlaces(internal);
  } catch (err) {
    console.warn('[aiImport] resolveCoordsViaPlaces falhou (segue com coords da IA):', err);
  }

  const trip = await tripsApi.create(internal.tripPayload);
  if (!trip?.id) throw new Error('Trip criada sem ID — verifique o console.');

  const errors = [];
  async function step(key, payload) {
    try {
      await cloudSave(trip.id, key, payload);
    } catch (err) {
      console.warn(`[aiImport] cloudSave('${key}') failed:`, err);
      errors.push({ key, error: err?.message || String(err) });
    }
  }

  await step('timeline',  internal.timeline);
  await step('itinerary', internal.itinerary);
  await step('hotels',    internal.hotels);
  await step('expenses',  internal.expenses);

  // Cotações que a IA devolveu — persistidas em trips/{id}/settings via
  // saveTripSettings. Best-effort: falhas só são logadas (a viagem segue
  // sem cotações automáticas — o user pode configurar depois).
  if (internal.currencyRates && Object.keys(internal.currencyRates).length) {
    try {
      await saveTripSettings(trip.id, { currencyRates: internal.currencyRates });
    } catch (err) {
      console.warn('[aiImport] saveTripSettings (currencyRates) falhou:', err);
      errors.push({ key: 'settings:currencyRates', error: err?.message || String(err) });
    }
  }

  // Checklist (sub-collection) — best-effort, falhas individuais não derrubam o import
  for (const item of internal.checklistItems || []) {
    try {
      await createChecklistItem(trip.id, item);
    } catch (err) {
      console.warn('[aiImport] createChecklistItem failed:', item.description, err);
      errors.push({ key: `checklist:${item.description}`, error: err?.message || String(err) });
    }
  }

  return { trip, errors, summary: internal.summary };
}

// ====================================================================
//  RESOLVE COORDS — corrige lat/lng dos itens importados via Places API
// ====================================================================
//
// A IA chuta lat/lng aproximadamente e às vezes erra grosseiro (ex: hotel
// no mar em vez de na rua). Quando o JSON da IA traz `title` + lat/lng mas
// SEM placeId, usamos `Place.searchByText({locationBias: círculo 500m})`
// pra achar o placeId real do lugar — mesmo padrão de `resolveItineraryPlace`
// no mapCanvas. Sobrescreve lat/lng e persiste placeId pro popup reusar e
// pra evitar re-resolução no boot do mapa.
//
// Cobre 3 categorias: stops (items do itinerário), stays (hotéis) e
// transports (endpoints from/to de cada rota).
//
// Idempotente: itens que já têm `placeId` são pulados. Falha individual
// cai pro lat/lng original da IA (melhor que nada).
//
// Throttling: processa em CHUNKS de 5 paralelos (Places API tem quota; vários
// itens em paralelo no boot somam). Aguarda cada chunk antes do próximo.
async function resolveCoordsViaPlaces(internal) {
  // Lazy import — `places.js` carrega script do Google Maps; não puxar fora
  // do browser (E2E em Node) nem antes do user disparar o import.
  let getGoogleMapsLibrary;
  try {
    ({ getGoogleMapsLibrary } = await import('../map/places.js'));
  } catch {
    return; // sem map module disponível, pula
  }

  let Place;
  try {
    const placesLib = await getGoogleMapsLibrary('places');
    Place = placesLib?.Place || window.google?.maps?.places?.Place;
  } catch {
    return; // Google Maps falhou ao carregar, pula
  }
  if (!Place) return;

  // Coleta TAREFAS de resolução: cada uma é uma função async que faz a
  // chamada e muta o blob in-place. Usar lista plana facilita o chunking.
  const tasks = [];

  // 5.1 — Itinerary items (stops)
  const overrides = internal?.itinerary?.overrides || {};
  for (const ddmm of Object.keys(overrides)) {
    const items = overrides[ddmm]?.items || [];
    for (const item of items) {
      if (item?.placeId) continue;
      if (!item?.title) continue;
      const lat = numOrNull(item.lat);
      const lng = numOrNull(item.lng);
      if (lat === null || lng === null) continue;
      tasks.push(async () => {
        const place = await searchPlaceFields(Place, item.title, lat, lng);
        if (!place) return;
        if (place.lat != null && place.lng != null) {
          item.lat = place.lat;
          item.lng = place.lng;
        }
        if (place.placeId) item.placeId = place.placeId;
      });
    }
  }

  // 5.2 — Hotels (stays). Mutamos tanto `internal.hotels.hotels[]` quanto
  // o `day.hotel` espelhado em `internal.timeline.days[].hotel` — eles SÃO
  // o mesmo objeto referenciado em 2 lugares (ver convertToInternal § 3.3),
  // então mutar um propaga, mas pra segurança casamos por nome+checkin.
  const hotelsList = internal?.hotels?.hotels || [];
  for (const hotel of hotelsList) {
    if (hotel?.placeId) continue;
    if (!hotel?.name) continue;
    const lat = numOrNull(hotel.lat);
    const lng = numOrNull(hotel.lng);
    if (lat === null || lng === null) continue;
    tasks.push(async () => {
      // Usa o endereço se houver, pra desambiguar hotéis com nome genérico
      // (ex: "Pousada Vila Mar" — vários no mesmo país).
      const query = hotel.address ? `${hotel.name}, ${hotel.address}` : hotel.name;
      const place = await searchPlaceFields(Place, query, lat, lng);
      if (!place) return;
      if (place.lat != null && place.lng != null) {
        hotel.lat = place.lat;
        hotel.lng = place.lng;
      }
      if (place.placeId) hotel.placeId = place.placeId;
      // Propaga pra cópia espelhada nos days (em alguns paths o renderHotels
      // lê de `day.hotel`, não de `hotels[]`).
      const timelineDays = internal?.timeline?.days || [];
      for (const d of timelineDays) {
        if (!d?.hotel) continue;
        const sameKey = `${d.hotel.name}|${d.hotel.checkin}|${d.hotel.checkout}`
          === `${hotel.name}|${hotel.checkin}|${hotel.checkout}`;
        if (sameKey) {
          if (place.lat != null) d.hotel.lat = place.lat;
          if (place.lng != null) d.hotel.lng = place.lng;
          if (place.placeId) d.hotel.placeId = place.placeId;
        }
      }
    });
  }

  // 5.3 — Transports (endpoints from/to). Cada rota tem `from`/`to` (nomes)
  // + fromLat/fromLng/toLat/toLng. Tenta resolver cada endpoint que tem
  // coord+nome mas sem placeId. Campos novos: fromPlaceId/toPlaceId.
  const timelineDays = internal?.timeline?.days || [];
  for (const day of timelineDays) {
    for (const route of (day?.routes || [])) {
      if (!route) continue;
      // Origem
      if (!route.fromPlaceId && route.from) {
        const lat = numOrNull(route.fromLat);
        const lng = numOrNull(route.fromLng);
        if (lat !== null && lng !== null) {
          tasks.push(async () => {
            const place = await searchPlaceFields(Place, route.from, lat, lng);
            if (!place) return;
            if (place.lat != null) route.fromLat = place.lat;
            if (place.lng != null) route.fromLng = place.lng;
            if (place.placeId) route.fromPlaceId = place.placeId;
          });
        }
      }
      // Destino
      if (!route.toPlaceId && route.to) {
        const lat = numOrNull(route.toLat);
        const lng = numOrNull(route.toLng);
        if (lat !== null && lng !== null) {
          tasks.push(async () => {
            const place = await searchPlaceFields(Place, route.to, lat, lng);
            if (!place) return;
            if (place.lat != null) route.toLat = place.lat;
            if (place.lng != null) route.toLng = place.lng;
            if (place.placeId) route.toPlaceId = place.placeId;
          });
        }
      }
    }
  }

  // Executa em chunks pra não estourar quota (ex: trip de 14 dias com 5
  // stops/dia + 2 hotéis + 3 transportes = ~80 tasks). 5 em paralelo é
  // conservador; cada call gasta ~1 unit do Places API.
  const CHUNK = 5;
  for (let i = 0; i < tasks.length; i += CHUNK) {
    const slice = tasks.slice(i, i + CHUNK);
    await Promise.all(slice.map((fn) =>
      fn().catch((err) => console.warn('[aiImport] resolveCoords task falhou:', err))
    ));
  }
}

// Busca um lugar via Place.searchByText (nome + locationBias 500m nas coords
// chutadas pela IA) e retorna {lat, lng, placeId} — ou null se nada achado.
async function searchPlaceFields(Place, textQuery, lat, lng) {
  try {
    const { places } = await Place.searchByText({
      textQuery,
      fields: ['id', 'location'],
      locationBias: { center: { lat, lng }, radius: 500 },
      maxResultCount: 1,
    });
    const top = places?.[0];
    if (!top) return null;
    const loc = top.location;
    const outLat = loc ? (typeof loc.lat === 'function' ? loc.lat() : loc.lat) : null;
    const outLng = loc ? (typeof loc.lng === 'function' ? loc.lng() : loc.lng) : null;
    return {
      placeId: top.id || '',
      lat: Number.isFinite(outLat) ? outLat : null,
      lng: Number.isFinite(outLng) ? outLng : null,
    };
  } catch (err) {
    console.warn('[aiImport] searchByText falhou:', textQuery, err);
    return null;
  }
}

// Cópia local do helper de aiImport.js (mantém este módulo independente).
function numOrNull(v) {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) && n !== 0 ? n : null;
}
